import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { APPROVAL_DIALOG } from "../../theme/tokens";
import type { StaffingIssue } from "../../utils/autoStaffing";
import { StyledDialog, StyledStyledDialog } from "./DialogUtils";
import { NurseRemovalContent } from "./NurseRemovalContent";
import type { RemoveStaffWindowHint } from "./RemoveStaff/removeStaffUtils";

interface NurseRemovalDialogProps {
	open: boolean;
	issue: StaffingIssue;
	/** The day the issue is on: its card finds it in the weeks the issue's positions point into. */
	date: DateTime;
	onClose: () => void;
}

/**
 * A recommendation's "remove someone from this shift": the Remove Staff panel on the issue's own day, opened on
 * the issue's window, taking exactly one pick (the recommendation is about one shift's overstaffing).
 */
export const NurseRemovalDialog: FC<NurseRemovalDialogProps> = ({ open, issue, date, onClose }) => {
	const [{ shiftNo: shiftIndex }] = issue.suggestions;
	const initialWindow = useMemo<RemoveStaffWindowHint>(
		() => ({ shiftIndex, hours: issue.shiftTimes ? { start: issue.shiftTimes.start, end: issue.shiftTimes.end } : null }),
		[shiftIndex, issue.shiftTimes],
	);

	return (
		<StyledDialog open={open} onClose={onClose} data-testid="nurse-removal-dialog">
			<StyledStyledDialog>
				<NurseRemovalContent
					date={date}
					onClose={onClose}
					showActual={false}
					source="nurse_removal_dialog"
					selectionMode="single"
					allowDateChange={false}
					initialWindow={initialWindow}
					// Content-sized under the popup's own cap, so the footer sits right under the grid.
					maxHeight={APPROVAL_DIALOG.MAX_HEIGHT}
				/>
			</StyledStyledDialog>
		</StyledDialog>
	);
};
